import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  CloudOff,
  Loader2,
  type LucideIcon,
  Plus,
  Search,
  UserRound,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  EhrConnector,
  PatientSearchConnectorError,
  PatientSearchItem,
} from "@elation/api-client/src/stitch-web/models";

import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";
import { Badge } from "@src/shared/components/ui/badge";
import { Button } from "@src/shared/components/ui/button";
import { Input } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/shared/components/ui/tooltip";
import {
  isStaleCursorError,
  useCursorPagination,
  useResetStaleCursor,
} from "@src/shared/hooks/useCursorPagination";
import { useDebouncedValue } from "@src/shared/hooks/useDebouncedValue";
import { cn } from "@src/shared/lib/utils";
import { getAssetUrl } from "@src/shared/utils/assets/getAssetUrl";
import { getDisplayErrorMessage } from "@src/shared/utils/errors";

import { usePatientsSearchQuery } from "../../../../data";
import {
  type CreatePatientFormValues,
  PatientEntity,
  type PatientSyncState,
} from "../../../../domain";
import { CreatePatientDialogV2 } from "../CreatePatientDialog";
import { usePatientOpener } from "../PatientSearch/hooks";

type PatientFilters = {
  q?: string;
};

const SEARCH_DEBOUNCE_MS = 300;
// Mirrors the server's `NGRAM_MIN_TOKEN_SIZE` (apps/server/src/shared/db/fulltext.ts).
// A shorter query sanitizes to zero usable full-text tokens, so the internal
// search leg falls back to an unfiltered listing while EHR connectors still
// run their full fan-out for nothing. Gate auto-search on it so a natural
// typing pause on a 1-character draft doesn't fire that fan-out; Enter still
// bypasses the gate for a deliberate short-query submission.
const MIN_SEARCHABLE_LENGTH = 2;

interface PatientsListV2Props {
  canCreatePatient?: boolean;
}

const EHR_LOGOS: Record<EhrConnector, { src: string; className: string }> = {
  elation: { src: "/images/elation-logo.svg", className: "h-3.5" },
  athenahealth: { src: "/images/athena-logo.svg", className: "h-3" },
  eclinicalworks: {
    src: "/images/eclinical-works-logo.svg",
    className: "h-2.5",
  },
  redoxfhirsandbox: { src: "/images/redox-logo-sandbox.svg", className: "h-3" },
};

function ConnectorErrorAlert({
  ehr,
  error,
}: {
  ehr: EhrConnector;
  error: PatientSearchConnectorError;
}) {
  const ehrLabel = PatientEntity.ehrConnectorLabel(ehr);

  return (
    <Alert
      variant="destructive"
      className="rounded-card border-destructive/40 bg-destructive/5"
      aria-live="polite"
    >
      <AlertCircle className="size-4" aria-hidden="true" />
      <AlertTitle>{ehrLabel} search failed</AlertTitle>
      <AlertDescription>{error.message}</AlertDescription>
    </Alert>
  );
}

function PatientsListSkeleton() {
  return (
    <VerticalFlexbox
      gap="md"
      className="rounded-card border border-border bg-surface p-card shadow-soft"
      aria-label="Loading patients"
      aria-busy="true"
    >
      {[0, 1, 2, 3].map((i) => (
        <HorizontalFlexbox key={i} alignItems="center" className="gap-3">
          <Skeleton className="size-10 rounded-full" />
          <VerticalFlexbox gap="sm" className="min-w-0 flex-1">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-64 max-w-full" />
          </VerticalFlexbox>
          <Skeleton className="h-6 w-20 rounded-full" />
        </HorizontalFlexbox>
      ))}
    </VerticalFlexbox>
  );
}

function EmptyPatientsList() {
  return (
    <VerticalFlexbox
      alignItems="center"
      justifyContent="center"
      className="min-h-[420px] rounded-card border border-dashed border-border bg-surface px-8 py-12 text-center"
    >
      <UserRound className="mb-4 size-9 text-primary" aria-hidden="true" />
      <h2 className="text-xl font-semibold text-surface-foreground">
        No patients found
      </h2>
      <p className="mt-2 max-w-[360px] text-sm leading-6 text-muted-foreground">
        Try a different search or create a new patient record.
      </p>
    </VerticalFlexbox>
  );
}

function ErrorPatientsList({ message }: { message?: string }) {
  return (
    <Alert
      variant="destructive"
      className="rounded-card border-destructive/40 bg-destructive/5 p-card"
    >
      <AlertCircle className="size-4" aria-hidden="true" />
      <AlertTitle>Unable to load patients</AlertTitle>
      <AlertDescription>
        {message ?? "Something went wrong retrieving patients."}
      </AlertDescription>
    </Alert>
  );
}

const SYNC_BADGE: Record<
  PatientSyncState,
  { variant: "success" | "warning"; icon: LucideIcon; className: string }
> = {
  synced: {
    variant: "success",
    icon: CheckCircle2,
    className:
      "gap-1 rounded-full border-transparent bg-success/10 px-1.5 py-0 text-[10px] text-success hover:bg-success/10",
  },
  not_synced: {
    variant: "warning",
    icon: CloudOff,
    className:
      "gap-1 rounded-full border-transparent bg-warning/10 px-1.5 py-0 text-[10px] text-warning hover:bg-warning/10",
  },
};

/**
 * Renders the sync state of a patient, or nothing when `syncState` reports
 * none — a patient whose system of record is Stitch has no sync relationship
 * to describe, so the row stays bare.
 *
 * The badge is wrapped in a plain `span` rather than being the tooltip trigger
 * itself because `Badge` does not forward a ref, which Radix's `asChild` slot
 * needs to anchor the tooltip.
 */
function PatientSyncBadge({ patient }: { patient: PatientSearchItem }) {
  const syncState = PatientEntity.syncState(patient);

  if (!syncState) {
    return null;
  }

  const { variant, icon: Icon, className } = SYNC_BADGE[syncState];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Badge variant={variant} className={className}>
            <Icon className="size-2.5" aria-hidden="true" />
            {PatientEntity.syncLabel(patient)}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] leading-5">
        {PatientEntity.syncDescription(patient)}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Renders the source EHR's logo, or nothing when the patient did not come from
 * an EHR.
 *
 * A null `ehr` means Stitch is the patient's system of record, which the design
 * represents by the absence of a logo rather than by any placeholder badge.
 */
function PatientEhrLogo({ patient }: { patient: PatientSearchItem }) {
  if (!patient.ehr) {
    return null;
  }

  const logo = EHR_LOGOS[patient.ehr];

  if (!logo) {
    return null;
  }

  const ehrLabel = PatientEntity.ehrConnectorLabel(patient.ehr);

  return (
    <span className="inline-flex h-6 items-center rounded-full px-1.5">
      <img
        src={getAssetUrl(logo.src)}
        alt={ehrLabel}
        className={cn("w-auto max-w-[120px] object-contain", logo.className)}
      />
    </span>
  );
}

function PatientRow({
  patient,
  isOpening,
  onPatientSelect,
}: {
  patient: PatientSearchItem;
  isOpening: boolean;
  onPatientSelect?: (patient: PatientSearchItem) => void;
}) {
  const name = PatientEntity.displayName(patient) ?? "Unknown patient";
  const metadata = PatientEntity.listMetadata(patient);

  return (
    <li aria-label={PatientEntity.accessibleListItemName(patient) ?? name}>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          "h-auto w-full justify-start gap-3 whitespace-normal rounded-none border-b border-border bg-transparent px-[18px] py-[13px] text-left font-normal shadow-none hover:bg-muted/40 hover:text-foreground focus-visible:bg-muted/40 focus-visible:ring-0 focus-visible:ring-offset-0",
          isOpening && "pointer-events-none opacity-70",
        )}
        disabled={!onPatientSelect || isOpening}
        onClick={() => onPatientSelect?.(patient)}
      >
        <Avatar className="size-10 border border-border bg-background">
          <AvatarFallback className="bg-primary/10 font-mono text-[11px] font-semibold uppercase text-primary">
            {PatientEntity.initials(patient)}
          </AvatarFallback>
        </Avatar>

        <VerticalFlexbox className="min-w-0 flex-1">
          <HorizontalFlexbox
            alignItems="center"
            flexWrap="wrap"
            className="gap-x-2 gap-y-1"
          >
            <span className="truncate text-sm font-semibold text-surface-foreground">
              {name}
            </span>
            <PatientEhrLogo patient={patient} />
            <PatientSyncBadge patient={patient} />
          </HorizontalFlexbox>
          {metadata ? (
            <p className="mt-1 font-mono text-[10.5px] text-muted-foreground">
              {metadata}
            </p>
          ) : null}
        </VerticalFlexbox>

        {isOpening ? (
          <Loader2
            className="size-4 shrink-0 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        ) : (
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </Button>
    </li>
  );
}

interface PatientsListContentProps {
  canCreatePatient: boolean;
  isLoading: boolean;
  // True while a request is in flight that ISN'T the very first fetch (i.e.
  // `patients` still holds `keepPreviousData`'s stale placeholder). Distinct
  // from `isLoading` so a debounced search or page turn can show a busy
  // affordance on the still-visible previous results instead of either
  // blanking to a skeleton or showing nothing at all.
  isFetching: boolean;
  isError: boolean;
  errorMessage?: string;
  onCreatePatientFromSearch: () => void;
  patients: PatientSearchItem[];
  searchTerm?: string;
  openingPatientKey: string | null;
  openPatient: (patient: PatientSearchItem) => void | Promise<void>;
}

function PatientsListContent({
  canCreatePatient,
  isLoading,
  isFetching,
  isError,
  errorMessage,
  onCreatePatientFromSearch,
  patients,
  searchTerm,
  openingPatientKey,
  openPatient,
}: PatientsListContentProps) {
  if (isLoading) return <PatientsListSkeleton />;
  if (isError) return <ErrorPatientsList message={errorMessage} />;
  if (patients.length === 0 && canCreatePatient && searchTerm?.trim()) {
    return (
      <Button
        type="button"
        variant="ghost"
        className="h-12 w-full justify-start gap-3 rounded-card border border-border bg-popover px-4 text-left text-[13px] font-medium text-surface-foreground shadow-soft hover:bg-accent hover:text-accent-foreground focus-visible:ring-1"
        onClick={onCreatePatientFromSearch}
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Plus className="size-4 stroke-[2.3]" aria-hidden="true" />
        </span>
        <span className="min-w-0 truncate">
          Create &quot;{searchTerm.trim()}&quot;
        </span>
      </Button>
    );
  }
  if (patients.length === 0) return <EmptyPatientsList />;

  return (
    <TooltipProvider delayDuration={150}>
      <ul
        aria-label="Patients"
        aria-busy={isFetching}
        className={cn(
          "overflow-hidden rounded-card border border-border bg-surface shadow-soft transition-opacity",
          isFetching && "opacity-60",
        )}
      >
        {patients.map((patient) => {
          const key = PatientEntity.searchResultKey(patient);
          return (
            <PatientRow
              key={key}
              patient={patient}
              isOpening={openingPatientKey === key}
              onPatientSelect={openPatient}
            />
          );
        })}
      </ul>
    </TooltipProvider>
  );
}

export function PatientsListV2({
  canCreatePatient = true,
}: PatientsListV2Props = {}) {
  const searchInputId = useId();
  const { openPatient, openingKey: openingPatientKey } =
    usePatientOpener("v2 patient list");
  const [filters, setFilters] = useState<PatientFilters>({});
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const lastCommittedQueryRef = useRef<string | null>(null);
  // Ranks by relevance once a real search is committed, browses by lastName
  // otherwise — derived from the committed filter (`filters.q`), not the
  // live draft in the input, so ranking only kicks in once a query is
  // actually submitted.
  const sort = PatientEntity.searchSortFor(filters.q ?? "");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createPatientInitialValues, setCreatePatientInitialValues] = useState<
    Partial<CreatePatientFormValues>
  >({});
  const {
    currentCursor,
    goToNextPage,
    goToPreviousPage,
    hasPrevPage,
    resetCursor,
  } = useCursorPagination();
  const patientsQuery = usePatientsSearchQuery({
    ...filters,
    ...sort,
    cursor: currentCursor,
  });
  const patientsInternal = useMemo(
    () => patientsQuery.data?.data.items ?? [],
    [patientsQuery.data],
  );
  const nextCursor = patientsQuery.data?.data.nextCursor ?? null;
  const isStaleCursor = isStaleCursorError({
    currentCursor,
    error: patientsQuery.error,
    isError: patientsQuery.isError,
  });
  const patients = patientsInternal;
  const searchErrors = patientsQuery.data?.data.errors ?? {};
  const isLoading = patientsQuery.isLoading;
  const isFetching = patientsQuery.isFetching;
  const isError = patientsQuery.isError && !isStaleCursor;
  const errorMessage =
    getDisplayErrorMessage(patientsQuery.error, {
      fallback: "Unable to load patients.",
    }) ?? undefined;
  const hasNextPage = Boolean(nextCursor);
  const searchErrorEntries = Object.entries(searchErrors) as Array<
    [EhrConnector, PatientSearchConnectorError]
  >;
  const submittedSearchTerm = filters.q?.trim() ?? "";
  const canCreateFromSearchDraft =
    submittedSearchTerm.length > 0 && query.trim() === submittedSearchTerm;

  useResetStaleCursor(isStaleCursor, resetCursor);

  // Commits a query value as the active search filter, keying the cursor
  // reset to the same commit so the two never drift apart. Guarded by
  // `lastCommittedQueryRef` so pressing Enter to commit immediately, followed
  // shortly after by the debounce timer catching up to that same value,
  // fires exactly one request instead of two.
  //
  // `allowShort` bypasses the `MIN_SEARCHABLE_LENGTH` gate — set for the
  // Enter/submit path so a deliberate short-query submission still goes
  // through, even though the debounce effect below won't auto-fire one.
  const commitQuery = useCallback(
    (rawValue: string, { allowShort = false } = {}) => {
      const trimmed = rawValue.trim();
      if (
        !allowShort &&
        trimmed.length > 0 &&
        trimmed.length < MIN_SEARCHABLE_LENGTH
      ) {
        return;
      }
      if (lastCommittedQueryRef.current === trimmed) return;
      lastCommittedQueryRef.current = trimmed;
      setFilters({ q: trimmed || undefined });
      resetCursor();
    },
    [resetCursor],
  );

  useEffect(() => {
    commitQuery(debouncedQuery);
  }, [commitQuery, debouncedQuery]);

  const ehrSearchErrors =
    searchErrorEntries.length > 0 ? (
      <VerticalFlexbox gap="sm" className="mb-3">
        {searchErrorEntries.map(([ehr, error]) => (
          <ConnectorErrorAlert key={ehr} ehr={ehr} error={error} />
        ))}
      </VerticalFlexbox>
    ) : null;

  return (
    <>
      <form
        role="search"
        className="grid gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          commitQuery(query, { allowShort: true });
        }}
      >
        <Label htmlFor={searchInputId} className="sr-only">
          Search patients
        </Label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id={searchInputId}
            name="q"
            value={query}
            type="search"
            placeholder="Search or add a patient"
            autoComplete="off"
            className="h-9 rounded-[9px] bg-surface pl-9"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </form>

      {ehrSearchErrors}
      <div className="mt-4">
        <PatientsListContent
          canCreatePatient={canCreatePatient}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          errorMessage={errorMessage}
          onCreatePatientFromSearch={() => {
            const parsedName = PatientEntity.parseName(submittedSearchTerm);
            setCreatePatientInitialValues({
              firstName: parsedName.first,
              lastName: parsedName.last,
            });
            setCreateDialogOpen(true);
          }}
          patients={patients}
          searchTerm={
            canCreateFromSearchDraft ? submittedSearchTerm : undefined
          }
          openingPatientKey={openingPatientKey}
          openPatient={openPatient}
        />
      </div>
      <CreatePatientDialogV2
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        initialValues={createPatientInitialValues}
      />

      {hasPrevPage || hasNextPage ? (
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="flex-end"
          gap="xs"
          className="mt-4"
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasPrevPage}
            onClick={goToPreviousPage}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasNextPage}
            onClick={() => goToNextPage(nextCursor)}
          >
            Next
          </Button>
        </HorizontalFlexbox>
      ) : null}
    </>
  );
}
